'use client';

import { Box } from '@mui/material';
import { memo, useDeferredValue, useEffect, useMemo, useState, useTransition } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { shallow } from 'zustand/shallow';
import { getIsHysaCalculatorActive } from '@repo/hooks/featureFlags/featureFlags';
import type { CategorySection as CategorySectionType } from '@repo/supergraph-schemas/roundup/base/types';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { dynamicWithRetry } from '../../DynamicWithRetry';
import { useGeo } from '../../GeoTargeting/GeoProvider';
import { EmptyState } from '../../OffersFilter/EmptyState';
import type { CompoundCalculatorSectionProps } from '../components/CalculatorSection/CompoundCalculatorSection';
import { useGetOffersFilterOffers, useOffersFilterStore } from '../components/OffersFilter/store';
import ProductCardCategorySectionsBase from '../components/ProductCardCategorySections';
import { SummaryTableSection as SummaryTableSectionBase } from '../components/SummaryTable/SummaryTableSection';
import { SeeMoreAccountsButton } from './ProductCardsSection/components/SeeMoreAccountsButton';

const TABLE_CONTAINER_SX = { marginBottom: { xs: '1.5rem', md: '2rem' } } as const;
const SummaryTableSection = memo(SummaryTableSectionBase);
const ProductCardCategorySections = memo(ProductCardCategorySectionsBase);

const CompoundCalculatorSection = dynamicWithRetry<CompoundCalculatorSectionProps>(
  () =>
    import('../components/CalculatorSection/CompoundCalculatorSection').then(mod => ({
      default: mod.CompoundCalculatorSection,
    })),
  { componentName: 'CompoundCalculatorSection' },
);

const DISPLAY_COUNT = 10;

export type HYSAProductsContentProps = {
  pageUrl: string;
  slug: string;
  isSeoBankingFFActive: boolean;
  categorySections: CategorySectionType[];
  deviceType?: MobileOrDesktop;
  highestAPY?: number;
  isApyRateTrendTreatment1Enabled?: boolean;
  isApyRateTrendTreatment2Enabled?: boolean;
  nationalAverageApy?: number;
};

export function HYSAProductsContent({
  pageUrl,
  slug,
  isSeoBankingFFActive,
  categorySections,
  deviceType,
  highestAPY,
  isApyRateTrendTreatment1Enabled = false,
  isApyRateTrendTreatment2Enabled = false,
  nationalAverageApy,
}: HYSAProductsContentProps) {
  const [displayCount, setDisplayCount] = useState(DISPLAY_COUNT);
  const [isPending, startTransition] = useTransition();
  const deferredDisplayCount = useDeferredValue(displayCount);
  const { regionCode } = useGeo();
  const offers = useGetOffersFilterOffers();
  const { isLoading, sortBy } = useOffersFilterStore(
    ({ isLoading, sortBy }) => ({ isLoading, sortBy }),
    shallow,
  );
  const isHysaCalculatorActive = getIsHysaCalculatorActive(slug);

  useEffect(() => {
    setDisplayCount(DISPLAY_COUNT);
  }, [offers.length]);

  const displayItems = useMemo(() => offers.slice(0, displayCount), [offers, displayCount]);
  const deferredDisplayItems = useMemo(
    () => offers.slice(0, Math.min(deferredDisplayCount, displayCount)),
    [offers, deferredDisplayCount, displayCount],
  );
  const totalCount = offers.length;
  const hasMore = totalCount > displayCount;
  const isEmpty = totalCount === 0 && !isLoading;

  return (
    <>
      {isEmpty ? (
        <EmptyState />
      ) : (
        <>
          <SummaryTableSection
            rows={displayItems}
            pageUrl={pageUrl}
            slug={slug}
            regionCode={regionCode}
            allowTruncatingRows={false}
            showSkeleton={isLoading}
            containerSx={TABLE_CONTAINER_SX}
            isFilterActive
            isApyRateTrendTreatment1Enabled={isApyRateTrendTreatment1Enabled}
            isApyRateTrendTreatment2Enabled={isApyRateTrendTreatment2Enabled}
          />
          <Box sx={{ mb: { xs: '1.5rem', md: '2rem' } }}>
            <SeeMoreAccountsButton
              label="See More Accounts"
              Icon={MdKeyboardArrowDown}
              onClick={() => startTransition(() => setDisplayCount(prev => prev + DISPLAY_COUNT))}
              displayCount={Math.min(displayCount, totalCount)}
              totalCount={totalCount}
              disabled={!hasMore || isPending}
            />
          </Box>
        </>
      )}
      {isHysaCalculatorActive && (
        <CompoundCalculatorSection deviceType={deviceType} defaultApy={highestAPY} />
      )}
      <ProductCardCategorySections
        categorySections={categorySections}
        isSeoBankingFFActive={isSeoBankingFFActive}
        regionCode={regionCode}
        showSkeleton={isLoading}
        offersListToFilterWith={deferredDisplayItems}
        isFilterEnabled
        isSorted={sortBy !== ''}
        isApyRateTrendTreatment1Enabled={isApyRateTrendTreatment1Enabled}
        isApyRateTrendTreatment2Enabled={isApyRateTrendTreatment2Enabled}
        nationalAverageApy={nationalAverageApy}
      />
    </>
  );
}
